import React, { useContext, useEffect, useState } from "react";
import { Button, Dropdown, Form, Input, message, Modal, Select, Tag } from "antd";
import dayjs from "dayjs";
import server from "../../../../server";
import Locale from "../../../../core/locale/locale";
import Grid from "../../../../common/table";
import AssetAllocationModal from "./utils/asset-allocation-modal";
import {
    Asset_Allocation_Sort_Field,
    Return_Asset_Status,
    Search_Asset_Allocation_Status
} from "../../../../constant/constant";
import { getName, removeUnderScore } from "../../../../utility/utility";
import { IAllocateAssetsFormData } from "../../asset-category/type/asset-category.types";
import { allocateAssetModalMapper } from "../../asset-category/action/asset-category.action";
import EmployeeAvatar from "../../../employee/avatar/employee-avatar";
import { AssetAllocationStatus } from "../../../../domains/models/asset-management/asset-allocation";
import SearchCriteria from "../../../common/search-criteria/search-criteria";
import { searchCriteria } from "./utils/search-criteria";
import { SearchParams } from "../../../common/search-criteria/type/search-criteria.type";
import EmployeeOverviewPopover from "../../../employee/overview/employee-overview-popover/employee-overview-popover";
import UserLoginContext from "../../../../context/userLoginContext";
import { TriangleAlert, Plus, Undo2, Ellipsis } from "lucide-react";
import LoadingSkeleton from "../../../common/loading-skeleton/loading-skeleton";
import '../../../../styles/component/asset/asset-allocation.scss'

const AssetAllocation = () => {
    const {user} = useContext(UserLoginContext);
    const [assignAssetForm] = Form.useForm();
    const [pageNumber, setPageNumber] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchPayload, setSearchPayload] = useState<any>({
        status: user.isAdmin ? [AssetAllocationStatus.ALLOCATED] :
            [AssetAllocationStatus.ALLOCATED, AssetAllocationStatus.MISSING, AssetAllocationStatus.RETURNED],
        sortBy: 'CREATED_ON',
        sortDirection: 'DESC'
    });
    const [sortField, setSortField] = useState("CREATED_ON");
    const [sortDirection, setSortDirection] = useState<"ASC" | "DESC" | undefined>("DESC");
    const pageSize = 10;
    const [state, setState] = useState({
        assignAssetModal: false,
        data: {
            assetAllocation: [],
            categories: []
        },
        returnStatus: "",
    })


    useEffect(() => {
        if (!user) {
            return;
        }
        fetchAssetCategories();
        getAllAssetAllocations(searchPayload, pageNumber);
    }, []);


    async function fetchAssetCategories() {
        try {
            const categories = await server.asset.category.list();
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    categories: categories
                }
            }))
        } catch (e) {
            console.log("Error while fetching categories", e);
        }
    }


    const handleNextPage = () => {
        const pageNum = pageNumber + 1;
        setPageNumber(prev => prev + 1);
        getAllAssetAllocations(searchPayload, pageNum);
    };

    const handlePrevPage = () => {
        if (pageNumber > 0) {
            const pageNum = pageNumber - 1;
            setPageNumber(prev => prev - 1);
            getAllAssetAllocations(searchPayload, pageNum);
        }
    };


    const handleSortChange = (field: string, direction: "ASC" | "DESC" | undefined) => {
        setSortField(field);
        setSortDirection(direction);
        const search = {...searchPayload, sortBy: field, sortDirection: direction};
        setSearchPayload(search);
        getAllAssetAllocations(search, pageNumber);
    };


    const handleAssignAssetModal = () => {
        assignAssetForm.resetFields();
        setState((prev) => ({
            ...prev,
            assignAssetModal: true
        }))
    }

    const handleAssignAssetModalClose = () => {
        assignAssetForm.resetFields();
        setState((prev) => ({
            ...prev,
            assignAssetModal: false
        }))
    }

    async function getAllAssetAllocations(payload: any, pageNumber: number) {
        setLoading(true);
        try {
            const allAssetAllocations = await server.asset.allocation.search(pageNumber, pageSize, payload);
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    assetAllocation: allAssetAllocations,
                }
            }))
        } catch (e) {
            console.log("Error while fetching asset allocations", e)
        } finally {
            setLoading(false);
        }
    }

    async function handleAssetReturn(record: any) {
        try {
            await server.asset.return(record.asset.id, state.returnStatus);
            await getAllAssetAllocations(searchPayload, 0);
        } catch (e) {
            console.log("Error while returning asset", e);
        }
    }

    const handleAssetAssignSubmit = async (formValues: IAllocateAssetsFormData) => {
        const payload = allocateAssetModalMapper(formValues);
        try {
            await server.asset.allocation.create(formValues.employeeId, payload);
            await getAllAssetAllocations(searchPayload, 0);
            handleAssignAssetModalClose();
            message.success("Asset assigned successfully!")
        } catch (e) {
            console.log("Error while submitting form", e)
        }
    }

    async function handleAssetMissed(record: any) {
        try {
            await server.asset.missing(record.asset.id);
            await getAllAssetAllocations(searchPayload, 0);
        } catch (e) {
            console.log("Error while submitting missing request", e)
        }
    }

    const handleAssetAllocationSearch = (params: SearchParams) => {
        let assetAllocationStatus;
        if (!params.status) {
            assetAllocationStatus = [AssetAllocationStatus.ALLOCATED];
        } else if (params.status === "ALL") {
            assetAllocationStatus = [
                AssetAllocationStatus.ALLOCATED,
                AssetAllocationStatus.MISSING,
            ];
        } else {
            assetAllocationStatus = [params.status];
        }
        const payload: any = {
            employeeName: params.employeeName,
            assetCode: params.assetCode,
            categoryId: params.categoryId,
            status: assetAllocationStatus,
            sortBy: sortField,
            sortDirection: sortDirection
        }

        if (params.assignedDateRange && Array.isArray(params.assignedDateRange)) {
            const [startDate, endDate] = params.assignedDateRange;
            payload.assignedDateRange = {
                startDate: startDate ? dayjs(startDate).format(Locale.dateFormat) : null,
                endDate: endDate ? dayjs(endDate).format(Locale.dateFormat) : null
            };
        }
        if (params.expectedReturnDateRange && Array.isArray(params.expectedReturnDateRange)) {
            const [startDate, endDate] = params.expectedReturnDateRange;
            payload.expectedReturnDateRange = {
                startDate: startDate ? dayjs(startDate).format(Locale.dateFormat) : null,
                endDate: endDate ? dayjs(endDate).format(Locale.dateFormat) : null
            };
        }
        setSearchPayload(payload);
        setPageNumber(0);
        getAllAssetAllocations(payload, 0);
    }

    const handleAssetAllocationSearchReset = () => {
        const defaultPayload = {
            status: [AssetAllocationStatus.ALLOCATED],
            sortBy: sortField,
            sortDirection: sortDirection
        };
        setSearchPayload(defaultPayload);
        setPageNumber(0);
        getAllAssetAllocations(defaultPayload, 0);
    }

    const columns = [
        ...(user?.isAdmin ? [
            {
                title: "Name",
                dataIndex: "employeeName",
                key: "employeeName",
                render: (_: any, record: any) => {
                    return (
                        <div className="employee-name-wrapper">
                            <EmployeeAvatar
                                name={{
                                    firstName: record?.employeeProfile?.name?.firstName,
                                    lastName: record?.employeeProfile?.name?.lastName
                                }}
                                thumbnailId={record?.employeeProfile?.profileThumbnailId}
                                size={25}
                            />
                            <EmployeeOverviewPopover
                                employeeId={record.employeeProfile.id}
                                uniqueKey={`${record?.employeeProfile.id}-${record.id}`}
                            >
                                <div className="employee-search-table-name">
                                    {getName(record.employeeProfile.name.firstName, record.employeeProfile.name.middleName, record.employeeProfile.name.lastName)}
                                </div>
                            </EmployeeOverviewPopover>
                        </div>
                    );
                }
            }
        ] : []),
        {
            title: "Category Name",
            dataIndex: "categoryName",
            key: "categoryName",
            render: (_: any, record: any) => {
                return (
                    record?.assetCategory?.name ? record?.assetCategory?.name : "-"
                )
            }
        },
        {
            title: "Brand Name",
            dataIndex: "brandName",
            key: "brandName",
            render: (_: any, record: any) => {
                return record?.asset?.brandName ? record.asset.brandName : "-"
            }
        },
        {
            title: "Asset Code",
            dataIndex: "assetCode",
            key: "assetCode",
            render: (_: any, record: any) => {
                return record?.asset?.assetCode ? record?.asset?.assetCode : "-"
            }
        },
        {
            title: "Assigned Date",
            dataIndex: "assignedDate",
            key: "assignedDate",
            sorter: true,
            sortField: Asset_Allocation_Sort_Field.assignedDate,
            sortOrder: sortField === Asset_Allocation_Sort_Field.assignedDate ?
                sortDirection === 'ASC' ? 'ascend'
                    : sortDirection === 'DESC' ?
                    'descend' : null : null,
            render: (_: any, record: any) => {
                return record?.assignedDate ? dayjs(record?.assignedDate).format(Locale.displayDate) : "-"
            }

        },
        {
            title: "Expected Return Date",
            dataIndex: "expectedReturnDate",
            sorter: true,
            sortField: Asset_Allocation_Sort_Field.expectedReturnDate,
            sortOrder: sortField === Asset_Allocation_Sort_Field.expectedReturnDate ?
                sortDirection === 'ASC' ? 'ascend'
                    : sortDirection === 'DESC' ?
                    'descend' : null : null,
            key: "expectedReturnDate",
            render: (_: any, record: any) => {
                return record?.expectedReturnDate ? dayjs(record?.expectedReturnDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: any) => {
                return <Tag className={`asset-allocation-status ${record.status.toLowerCase()}`}>
                    {record?.status ? removeUnderScore(record.status) : "-"}
                </Tag>

            }
        },
        ...(user?.isAdmin ?
            [{
                title: "Action",
                dataIndex: "action",
                key: "action",
                render: (_: any, record: any) => {
                    const menuItems=[
                        {
                            key:0,
                            label:'Return Asset',
                            icon:<Undo2
                                className={`action-edit action-return-asset 
                                ${record.status === "RETURNED" ? 'action-disabled' : ''}`}/>,
                            onClick:()=>{
                                Modal.confirm({
                                      title:'Mark Asset Return Status',
                                      content: <>
                                          <h6 className={''}>Select status</h6>
                                          <Select
                                          onChange={(value: any) => setState((prev) => ({
                                              ...prev,
                                              returnStatus: value
                                          }))}
                                          placeholder={'Select Asset Status'}>
                                          {(Object.keys(Return_Asset_Status) as Array<keyof typeof Return_Asset_Status>).map((key) =>
                                              <Select.Option value={key} key={key}>
                                                  {Return_Asset_Status[key]}
                                              </Select.Option>
                                          )}
                                      </Select>
                                          </>,
                                    onOk:() => handleAssetReturn(record)
                                })
                            } ,
                            disabled:record.status === "RETURNED"

                        },
                        {
                            key:1,
                            label:'Asset Missing',
                            icon:  <TriangleAlert
                                className={`action-mark-missing action-danger
                                 ${record.status === "MISSING" ? 'action-disabled' : ''} `}
                            />,
                            onClick:()=>{
                                Modal.confirm({
                                    title:'Missing Asset Confirmation',
                                    content: `Are you sure ${record?.asset?.assetCode} is missed?`,
                                    onOk:() => handleAssetReturn(record),
                                    okText:'Yes'
                                })
                            } ,
                            disabled: record.status === "MISSING"
                        }
                    ]
                    return (
                        <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                            <Ellipsis className={'pn-table-actions-icon'}/>
                        </Dropdown>
                    )

                },
            }]
            : []),
    ]

    return (
        <>

            <div className={'asset-allocation'}>
                <h3 className={'asset-allocation heading'}>Asset Allocation</h3>

                {user?.isAdmin &&
                <Button type={'primary'} onClick={handleAssignAssetModal} className={'add-btn'} icon={<Plus/>}> Allocate
                    Asset</Button>
                }

            </div>
            {
                user?.isAdmin && <SearchCriteria
                    title={"Search Asset Allocations"}
                    onSearch={handleAssetAllocationSearch}
                    onReset={handleAssetAllocationSearchReset}
                    searchCriteria={searchCriteria}
                    showReset={true}
                    renderTopContent={({values, onChange}) => (
                        <>
                            <div>
                                <label className={"label"}> Name</label>
                                <Input
                                    placeholder="Enter Name"
                                    value={values.employeeName || ''}
                                    onChange={(e) => onChange('employeeName', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className={"label"}> Asset Code</label>
                                <Input
                                    placeholder="Enter Asset Code"
                                    value={values.assetCode || ''}
                                    onChange={(e) => onChange('assetCode', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className={"label"}>Select Category</label>
                                <Select
                                    placeholder={"Select Category"}
                                    onChange={(val: any) => onChange('categoryId', val)}
                                    value={values.categoryId}
                                    allowClear
                                    className={"label"}
                                >
                                    {state.data.categories.map((item: any) => (
                                        <Select.Option key={item.id} value={item.id}>
                                            {item.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <label className={"label"}>Select Status</label>
                                <Select
                                    placeholder={"Select Asset Status"}
                                    onChange={(val: any) => onChange('status', val)}
                                    value={values.status ?? AssetAllocationStatus.ALLOCATED}
                                    allowClear
                                    className={"label"}
                                >
                                    <Select.Option key="ALL" value="ALL">
                                        Select All
                                    </Select.Option>
                                    {(Object.keys(Search_Asset_Allocation_Status) as Array<keyof typeof Search_Asset_Allocation_Status>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Search_Asset_Allocation_Status[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            </div>
                        </>
                    )}
                />
            }

            <div className={'asset-allocation-table'}>
                <LoadingSkeleton loading={loading}>
                    <Grid
                        bordered={true}
                        dataSource={state.data.assetAllocation}
                        columns={columns}
                        pageNumber={pageNumber}
                        paginationButton={true}
                        pageSize={pageSize}
                        onChange={(pagination: any, filters: any, sorter: any) => {
                            let direction: "ASC" | "DESC" | undefined;

                            switch (sorter.order) {
                                case "ascend":
                                    direction = "ASC";
                                    break;
                                case "descend":
                                    direction = "DESC";
                                    break;
                                default:
                                    direction = undefined;
                            }
                            const backendField = sorter.column?.sortField ?? Asset_Allocation_Sort_Field.createdOn;

                            if (!backendField) return;

                            handleSortChange(backendField, direction);
                        }}
                        handleNextPage={handleNextPage}
                        handlePrevPage={handlePrevPage}
                    />
                </LoadingSkeleton>

            </div>

            <AssetAllocationModal
                assignAssetForm={assignAssetForm}
                assignAssetModal={state.assignAssetModal}
                handleAssignAssetModalClose={handleAssignAssetModalClose}
                handleAssetAssignSubmit={handleAssetAssignSubmit}
            />
        </>
    )
}

export default AssetAllocation;
